/** Frame cadence of the attack spritesheets (~12fps) — the grid every battle FX steps on. */
export const FX_FRAME_MS = 83;

/** A loaded spritesheet: the raw <img>, or an offscreen canvas standing in for it. */
export type SpriteSource = HTMLImageElement | HTMLCanvasElement;

// Palette-preview seam. Live slots are held by two services, so swapping sheets on
// a toggle has to happen where frames are read, not where they're loaded.
let sheetResolver: ((sheet: SpriteSource) => SpriteSource) | null = null;

export function setSheetResolver(fn: ((sheet: SpriteSource) => SpriteSource) | null): void {
  sheetResolver = fn;
}

export interface SpriteAnimationConfig {
  spritesheetUrl: string;
  /** Array of (x,y) within the spritesheet */
  frames: readonly (readonly number[])[];
  frameWidth: number;
  frameHeight: number;
  frameDurationMs: number;
  loop: boolean;
  /** Topmost non-transparent source row (idle sprites only); drives the HP-bar offset. */
  contentTop?: number;
  /** Bottommost non-transparent source row (idle sprites only); drives the floor-grounding shift. */
  contentBottom?: number;
  /** Leftmost non-transparent source column (idle sprites only); bounds the hover hitbox. */
  contentLeft?: number;
  /** Rightmost non-transparent source column, exclusive (idle sprites only). */
  contentRight?: number;
  /** Footprint center column (ground-shadow sprites only); aligns the blob under the mon. */
  cx?: number;
  /** Callback fired when a non-looping animation completes */
  onComplete?: () => void;
  /** Pause when AnimationSpeedService.skipAnimations() is on (battle-scoped consumers) */
  freezeOnSkip?: boolean;
}

export function resolveSheet(sheet: SpriteSource): SpriteSource {
  return sheetResolver ? sheetResolver(sheet) : sheet;
}

const sheetKeys = new WeakMap<HTMLCanvasElement, string>();
let nextSheetKey = 0;

/** Stable cache identity for a sheet; offscreen canvases have no `src` to key off. */
export function sheetKey(sheet: SpriteSource): string {
  if (sheet instanceof HTMLImageElement) return sheet.src;
  let key = sheetKeys.get(sheet);
  if (!key) sheetKeys.set(sheet, (key = `canvas:${++nextSheetKey}`));
  return key;
}

/** A sheet still decoding has nothing to read back yet; a canvas is always drawable. */
export function sheetReady(sheet: SpriteSource): boolean {
  return sheet instanceof HTMLImageElement ? sheet.complete && sheet.naturalWidth > 0 : true;
}

export interface FrameRenderData {
  image: SpriteSource;
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
}

/**
 * Paint one frame onto a canvas, sizing the backing store to the source frame
 * in the same pass so a cold load can't draw into a stale 0×0 canvas. A null
 * frame clears; a missing canvas is a no-op.
 */
export function drawSpriteFrame(
  canvas: HTMLCanvasElement | null | undefined,
  frame: FrameRenderData | null,
): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (!frame) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    return;
  }

  if (canvas.width !== frame.sourceWidth) canvas.width = frame.sourceWidth;
  if (canvas.height !== frame.sourceHeight) canvas.height = frame.sourceHeight;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    frame.image,
    frame.sourceX,
    frame.sourceY,
    frame.sourceWidth,
    frame.sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );
}

/** Parse `#rrggbb` into an RGB triple for canvas pixel writes. */
export function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.replace('#', ''), 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

export interface AnimationSlot {
  config: SpriteAnimationConfig;
  image: SpriteSource;
  elapsedTime: number;
  currentFrameIndex: number;
  hasCompleted: boolean;
  /** Last frame index published to this slot's frame signal (dynamic slots only) */
  lastEmittedFrameIndex?: number;
}

